import React, { useState, useEffect } from 'react';
import { getStripe, createCheckoutSession } from '../../utils/stripeConfig';
import { useCurrency } from '../../contexts/CurrencyContext';
import { calculateWinicoins } from '../../utils/winicoinsUtils';
import useAuthListener from '../../hooks/useAuthListener';
import { useLanguage } from '../../contexts/LanguageContext';
import { auth, googleProvider, appleProvider } from '../../firebase/firebase';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import logo from '../../assets/svgs/logo circular1.png';
import './DiscoverReturns.css';

const DiscoverReturns = () => {
  const [selectedAmount, setSelectedAmount] = useState('144');
  const [selectedDuration, setSelectedDuration] = useState('12');
  const [calculatedResult, setCalculatedResult] = useState(1152);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { user } = useAuthListener();

  // Sign-in popup state
  const [showSignInPopup, setShowSignInPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);

  // Use language context
  const { translations: t } = useLanguage();

  // Use shared currency context
  const {
    selectedCurrency,
    setSelectedCurrency,
    availableCurrencies,
    isLoadingCurrencies,
    convertToSelectedCurrency
  } = useCurrency();

  // Investment amounts from the plan cards
  const investmentAmounts = [
    { value: '72', label: '$72' },
    { value: '144', label: '$144' },
    { value: '360', label: '$360' },
    { value: '720', label: '$720' },
    { value: '1440', label: '$1,440' },
    { value: '3600', label: '$3,600' },
    { value: '7200', label: '$7,200' },
    { value: '14400', label: '$14,400' },
    { value: '36000', label: '$36,000' },
    { value: '72000', label: '$72,000' },
    { value: '144000', label: '$144,000' },
    { value: '360000', label: '$360,000' }
  ];

  // Duration options
  const durationOptions = [
    { value: '6', label: '6 months' },
    { value: '12', label: '12 months' },
    { value: '24', label: '24 months' }
  ];

  // Multiplier mapping based on duration
  const getMultiplier = (duration) => {
    switch (duration) {
      case '6': return 4;  // ×4 for 6 months
      case '12': return 8; // ×8 for 1 year
      case '24': return 12; // ×12 for 2 years
      default: return 8;
    }
  };

  // Get plan title based on amount
  const getPlanTitle = (amount) => {
    const amountNum = parseInt(amount, 10);
    switch (amountNum) {
      case 72: return 'Basic';
      case 144: return 'Starter';
      case 360: return 'Growth';
      case 720: return 'Advanced';
      case 1440: return 'Professional';
      case 3600: return 'Enterprise';
      case 7200: return 'Premium';
      case 14400: return 'Ultimate';
      case 36000: return 'Elite';
      case 72000: return 'Royal';
      case 144000: return 'Empire';
      case 360000: return 'Legacy';
      default: return 'Custom';
    }
  };

  // Format amount for display
  const formatAmount = (amount) => {
    const converted =
      typeof convertToSelectedCurrency === 'function'
        ? convertToSelectedCurrency(Number(amount) || 0)
        : (Number(amount) || 0);

    return formatNumberWithCommas(converted, 2);
  };

  // Calculate result whenever amount, duration, or currency changes
  useEffect(() => {
    const amount = parseInt(selectedAmount, 10);
    const multiplier = getMultiplier(selectedDuration);
    const result = amount * multiplier;
    setCalculatedResult(result);
  }, [selectedAmount, selectedDuration, selectedCurrency]);

  const formatNumberWithCommas = (value, fractionDigits = 2) => {
    if (value == null || isNaN(value)) return '0';
    const isInt = Number.isInteger(Number(value));
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: isInt ? 0 : 0,
      maximumFractionDigits: fractionDigits,
    }).format(Number(value));
  };

  // Get ISO code (or name) for the currently selected currency (3-letter format)
  const getSelectedCurrencyIso = () => {
    const match = Array.isArray(availableCurrencies)
      ? availableCurrencies.find((currency) => currency.name === selectedCurrency)
      : null;
    return (match && match.name) ? match.name : (selectedCurrency || 'USD');
  };

  // Handle payment processing
  const handleInvestNow = async () => {
    if (isProcessing) return;

    const currentUser = user || auth.currentUser;
    if (!currentUser) {
      setShowSignInPopup(true);
      return;
    }

    setIsProcessing(true);

    try {
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      const originalAmount = parseInt(selectedAmount, 10);
      const convertedAmount = convertToSelectedCurrency(originalAmount);
      const winicoinsToEarn = calculateWinicoins(originalAmount);

      // ✅ NEW call signature: pass a single object
      const { sessionId } = await createCheckoutSession({
        amount: convertedAmount,
        planTitle: getPlanTitle(selectedAmount),
        duration: selectedDuration,
        multiplier: getMultiplier(selectedDuration),
        currency: getSelectedCurrencyIso(), // e.g. USD, AED
        customerEmail: currentUser.email,
        metadata: {
          userId: currentUser.uid,
          originalAmount,
          winicoinsToEarn,
          purchaseType: 'investment',
          planTitle: getPlanTitle(selectedAmount),
        },
      });

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        console.error('Stripe error:', error);
        alert('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment processing failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Sign-in handlers
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setIsSigningIn(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setShowSignInPopup(false);
      setEmail('');
      setPassword('');
      setIsSigningIn(false);
      setTimeout(() => {
        handleInvestNow();
      }, 100);
    } catch (err) {
      setAuthError(err.message);
      setIsSigningIn(false);
    }
  };

  const handleGoogleLogin = async () => {
    setAuthError('');
    setIsSigningIn(true);
    try {
      await signInWithPopup(auth, googleProvider);
      setShowSignInPopup(false);
      setIsSigningIn(false);
      setTimeout(() => {
        handleInvestNow();
      }, 100);
    } catch (err) {
      setAuthError(err.message);
      setIsSigningIn(false);
    }
  };

  const handleAppleLogin = async () => {
    setAuthError('');
    setIsSigningIn(true);
    try {
      await signInWithPopup(auth, appleProvider);
      setShowSignInPopup(false);
      setIsSigningIn(false);
      setTimeout(() => {
        handleInvestNow();
      }, 100);
    } catch (err) {
      setAuthError(err.message);
      setIsSigningIn(false);
    }
  };

  const closeSignInPopup = () => {
    setShowSignInPopup(false);
    setEmail('');
    setPassword('');
    setAuthError('');
    setIsSigningIn(false);
  };

  return (
    <section className="discover-returns-section">
      <div className="discover-returns-container">
        <div className="discover-returns-header">
          <h2 className="discover-returns-title">{t.discoverReturns?.title || "Discover Your Potential Returns"}</h2>
          <p className="discover-returns-subtitle">
            {t.discoverReturns?.subtitle || "Calculate your potential earnings with our smart strategies"}
          </p>
        </div>

        <div className="discover-returns-content">
          <div className="calculator-controls">
            <div className="control-group">
              <label htmlFor="amount-select" className="control-label">
                {t.discoverReturns?.controls?.moneyAmount || "Money Amount"}
              </label>
              <div className="custom-select-wrapper">
                <select
                  id="amount-select"
                  value={selectedAmount}
                  onChange={(e) => setSelectedAmount(e.target.value)}
                  className="custom-select"
                >
                  {investmentAmounts.map((amount) => (
                    <option key={amount.value} value={amount.value}>
                      {amount.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="control-group">
              <label htmlFor="duration-select" className="control-label">
                {t.discoverReturns?.controls?.duration || "Duration"}
              </label>
              <div className="custom-select-wrapper">
                <select
                  id="duration-select"
                  value={selectedDuration}
                  onChange={(e) => setSelectedDuration(e.target.value)}
                  className="custom-select"
                >
                  {durationOptions.map((duration) => (
                    <option key={duration.value} value={duration.value}>
                      {duration.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="control-group">
              <label htmlFor="currency-select" className="control-label">
                {t.discoverReturns?.controls?.currency || "Currency"}
              </label>
              <div className="custom-select-wrapper">
                <select
                  id="currency-select"
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  className="custom-select"
                  disabled={isLoadingCurrencies}
                >
                  {isLoadingCurrencies ? (
                    <option value="">{t.discoverReturns?.controls?.loadingCurrencies || "Loading currencies..."}</option>
                  ) : (
                    availableCurrencies.map((currency) => (
                      <option key={currency.id} value={currency.name}>
                        {currency.name} ({currency.iso})
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
          </div>

          <div
            className="returns-visualization"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="visualization-bg">
              <div className="floating-shape shape-1"></div>
              <div className="floating-shape shape-2"></div>
              <div className="floating-shape shape-3"></div>
            </div>

            <div className="returns-card">
              <div className="returns-header">
                <h3 className="plan-name">{getPlanTitle(selectedAmount)} {t.discoverReturns?.card?.plan || "Plan"}</h3>
                <div className="returns-multiplier">
                  {(getMultiplier(selectedDuration) * 100)}% {t.discoverReturns?.card?.return || "Return"}
                </div>
              </div>

              <div className="returns-details">
                <div className="detail-item">
                  <span className="detail-label">{t.discoverReturns?.card?.amount || "Amount"}</span>
                  <span className="detail-value">{formatAmount(parseInt(selectedAmount, 10))} {getSelectedCurrencyIso()}</span>
                </div>

                <div className="growth-animation">
                  <div className="growth-arrow">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 20V4M12 4L5 11M12 4L19 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="growth-text">{getMultiplier(selectedDuration)}x {t.discoverReturns?.card?.growth || "Growth"}</div>
                </div>

                <div className="detail-item">
                  <span className="detail-label">{t.discoverReturns?.card?.duration || "Duration"}</span>
                  <span className="detail-value">
                    {durationOptions.find(d => d.value === selectedDuration)?.label}
                  </span>
                </div>
              </div>

              <div className="final-returns">
                <div className="returns-amount">{formatAmount(calculatedResult)} {getSelectedCurrencyIso()}</div>
                <div className="returns-label">{t.discoverReturns?.card?.totalReturns || "Total Returns"}</div>
              </div>

              <button
                className={`invest-now-btn ${isHovered ? 'hovered' : ''}`}
                onClick={handleInvestNow}
                disabled={isProcessing}
              >
                <span className="btn-text">
                  {isProcessing
                    ? (t.discoverReturns?.button?.processing || 'Processing...')
                    : !user
                      ? (t.discoverReturns?.button?.signInToStart || 'Sign in to Start')
                      : (t.discoverReturns?.button?.startNow || 'Start Now')}
                </span>
                <span className="investment-btn-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Sign-in Popup */}
      {showSignInPopup && (
        <div className="signin-popup-overlay" onClick={closeSignInPopup}>
          <div className="signin-popup" onClick={(e) => e.stopPropagation()}>
            <div className="signin-popup-header">
              <h2>{t.shortInvestment?.signInPopup?.title || 'Sign In to Continue'}</h2>
              <button className="signin-popup-close" onClick={closeSignInPopup}>
                ×
              </button>
            </div>

            <div className="signin-popup-content">
              <div className="signin-form-container">
                <div className="signin-form-wrapper">
                  <div className="signin-header">
                    <div className="signin-logo-border-radius">
                      <img src={logo} alt="logo" />
                    </div>
                    <h1>{t.shortInvestment?.signInPopup?.welcome || 'Welcome to iWinto'}</h1>
                  </div>

                  <form onSubmit={handleEmailLogin} className="signin-form">
                    <div className="form-group">
                      <input
                        type="email"
                        placeholder={t.shortInvestment?.signInPopup?.emailPlaceholder || 'Email'}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="password"
                        placeholder={t.shortInvestment?.signInPopup?.passwordPlaceholder || 'Password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <button type="submit" className="signin-button" disabled={isSigningIn}>
                      {isSigningIn
                        ? (t.shortInvestment?.signInPopup?.processing || 'Processing...')
                        : (t.shortInvestment?.signInPopup?.loginButton || 'Log in')}
                    </button>
                    {!!authError && <p className="signin-error">{authError}</p>}
                  </form>

                  <div className="signin-divider"><span>{t.shortInvestment?.signInPopup?.orContinueWith || 'Or Continue With'}</span></div>
                  <div className="signin-social-buttons">
                    <button onClick={handleGoogleLogin} className="signin-google-button" disabled={isSigningIn}>
                      <img
                        src="https://www.google.com/favicon.ico"
                        alt="Google"
                        className="signin-google-icon"
                      />
                      Google
                    </button>
                    <button onClick={handleAppleLogin} className="signin-apple-button" disabled={isSigningIn}>
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg"
                        alt="Apple"
                        className="signin-apple-icon"
                      />
                      Apple
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DiscoverReturns;
